import { HistoryItem } from '../history';
import { AnnotationClassName } from '../snapshot/classificationClass';
import { ClassificationDetection } from '../snapshot/classificationDetection';
import { SnapshotCameraPosition } from '../snapshot/snapshotImage';
import { Timestamped } from '../timestamped';
import { DsiAdditionalClassifications } from './dsiAdditionalClassifications';
import { DsiAnnotation } from './dsiAnnotation';
import { z } from 'zod';

export const LabellingLoadedType = z.enum(['empty', 'unsure', 'loaded', 'faulty']);
export type LabellingLoadedType = z.infer<typeof LabellingLoadedType>;

export const LabellingBundleType = z.enum(['noBundle', 'unsure', 'bundle']);
export type LabellingBundleType = z.infer<typeof LabellingBundleType>;

export const BundleData = z.object({
    size: z.number().or(z.literal('?')),
});
export type BundleData = z.infer<typeof BundleData>;

export const DsiLabellingImageRating = z.enum([
    // Image is okay and can be processed
    'ok',
    // User is unsure about at least one of the detections/annotations
    'unsure',
    // Image can't be used (e.g. camera blocked/broken)
    'faulty',
]);
export type DsiLabellingImageRating = z.infer<typeof DsiLabellingImageRating>;

export const DsiLabellingProcessingState = z.enum([
    // Detections haven't been processed
    'unprocessed',
    // Detections have been processed, but manual annotations still have to be done (unused for now)
    'processed-detections',
    // Detections have been processed and all manual annotations have been added
    'processed-annotations',
]);
export type DsiLabellingProcessingState = z.infer<typeof DsiLabellingProcessingState>;

export const DsiLabellingDraft = z.object({
    correlationGuid: z.string(),
    event: z.string(),
    storeGuid: z.string().nullish(),
    sessionGuid: z.string(),
    sessionStartedOn: z.coerce.date().nullish(),
    sessionEndedOn: z.coerce.date().nullish(),
    url: z.string(),
    imageWidth: z.number(),
    imageHeight: z.number(),
    imageRating: DsiLabellingImageRating.nullish(),
    imageProcessingState: DsiLabellingProcessingState.default('unprocessed').nullish(),
    cameraPosition: SnapshotCameraPosition,
    labellingLoaded: LabellingLoadedType.nullish(),
    labellingBundles: LabellingBundleType.nullish(),
    labellingBundlesAmounts: BundleData.array().nullish(),
    detections: ClassificationDetection.array().nullish(),
    deletedDetectionHashes: z.string().array().nullish(),
    detectionProbabilityThreshold: z.number().min(0).max(1).nullish(),
    annotations: DsiAnnotation.array()
        .default(() => [])
        .nullish(),
    /** List of "all currently known" annotation classes, except those which are disabled */
    allAnnotationClasses: AnnotationClassName.array().nullish(),
    /** List of "disabled" annotation classes, which are explicitly hidden & treated as always wrong */
    disabledAnnotationClasses: z.string().array().nullish(),
    sessionSnapshotIndex: z.number().nullish(),
    sessionSnapshotsTotal: z.number().nullish(),
    additionalImageClassifications: z.record(z.string(), DsiAdditionalClassifications).nullish(),
    history: HistoryItem.array().nullish(),
});
export type DsiLabellingDraft = z.infer<typeof DsiLabellingDraft>;

export const DsiLabellingData = Timestamped.and(DsiLabellingDraft);
export type DsiLabellingData = z.infer<typeof DsiLabellingData>;
